import {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
} from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import api from '../services/api';
import { TOKEN_KEY } from '../services/Auth';

const adminRoles = ['ADMINISTRADOR', 'GERENTE'];

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const history = useHistory();

  const [user, setUser] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const isAuthenticated = !!user?.email;

  const signIn = useCallback(
    async ({ email, password }) => {
      try {
        const { data } = await api.post('session', { email, password });
        const { token, user: userData } = data;

        const isAdmin = adminRoles.includes(userData?.role);
        if (!isAdmin) {
          toast.warn('Usuário não tem permissão para acessar essa página');
          return;
        }

        localStorage.setItem(TOKEN_KEY, token);
        api.defaults.headers['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        history?.push('/integradores');
      } catch (error) {
        const msg = error?.response?.data?.message || 'Falha ao entrar';
        toast.error(msg);
      }
    },
    [history]
  );

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers['Authorization'];
    setUser(undefined);
    history?.push('/login');
  }, [history]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      api.defaults.headers['Authorization'] = `Bearer ${token}`;
      const { data } = await api.get('/users/profile');
      const isAdmin = adminRoles.includes(data?.role);
      if (!isAdmin) {
        throw new Error('no-permission');
      }
      setUser(data);
    } catch {
      signOut();
    } finally {
      setLoading(false);
    }
  }, [signOut]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  return (
    <AuthContext.Provider
      value={{ signIn, signOut, isAuthenticated, user, refreshUser, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
